/**
 * Questions à revoir — écran dédié aux questions retirées de la rotation.
 *
 * Chacune porte le motif de son retrait (colonne review_note), issu de l'audit
 * des 3 138 questions de septembre 2026. L'écran permet de corriger l'énoncé,
 * les choix, la bonne réponse, puis de remettre la question en jeu — ou de la
 * supprimer définitivement quand elle est irrécupérable.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaCheck, FaTrash, FaSearch, FaExclamationTriangle } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../api/axios';

const CATEGORIES   = ['competition', 'club', 'joueur', 'histoire'];
const DIFFICULTES  = ['facile', 'moyen', 'difficile'];

function QuestionCard({ q, onSaved, onDeleted }) {
  const [ouvert, setOuvert]   = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState({
    intitule:      q.intitule,
    choix:         Array.isArray(q.choix) ? [...q.choix] : JSON.parse(q.choix || '[]'),
    bonne_reponse: q.bonne_reponse,
    difficulte:    q.difficulte,
    categorie:     q.categorie,
  });

  const modifie = form.intitule !== q.intitule
    || form.bonne_reponse !== q.bonne_reponse
    || form.difficulte !== q.difficulte
    || form.categorie !== q.categorie
    || JSON.stringify(form.choix) !== JSON.stringify(Array.isArray(q.choix) ? q.choix : JSON.parse(q.choix || '[]'));

  const enregistrer = async (reactiver) => {
    if (!form.intitule.trim()) { toast.error('L\'énoncé ne peut pas être vide'); return; }
    if (form.choix.some((c) => !String(c).trim())) { toast.error('Les 4 choix doivent être remplis'); return; }

    setSaving(true);
    try {
      await api.put(`/questions/${q.id}`, {
        intitule:      form.intitule.trim(),
        choix:         form.choix.map((c) => String(c).trim()),
        bonne_reponse: form.bonne_reponse,
        difficulte:    form.difficulte,
        categorie:     form.categorie,
        ...(reactiver ? { is_active: true } : {}),
      });
      toast.success(reactiver ? 'Question corrigée et remise en jeu !' : 'Correction enregistrée');
      if (reactiver) onSaved(q.id);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const supprimer = async () => {
    if (!window.confirm('Supprimer définitivement cette question ? Cette action est irréversible.')) return;
    setSaving(true);
    try {
      await api.delete(`/questions/${q.id}`);
      toast.success('Question supprimée');
      onDeleted(q.id);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 overflow-hidden">
      {/* En-tête cliquable */}
      <button
        onClick={() => setOuvert((o) => !o)}
        className="w-full text-left px-4 py-3 hover:bg-white/[0.03] transition-colors"
      >
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <p className="text-white text-sm font-semibold leading-snug">{q.intitule}</p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-white/5 text-white/40">
                {q.categorie}
              </span>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-white/5 text-white/40">
                {q.difficulte}
              </span>
            </div>
          </div>
          <span className="text-white/30 text-xs mt-1">{ouvert ? '▲' : '▼'}</span>
        </div>

        {/* Motif du retrait — l'information la plus utile, toujours visible */}
        <div className="flex items-start gap-2 mt-2.5 p-2.5 rounded-lg"
          style={{ background: 'rgba(240,180,41,0.07)', border: '1px solid rgba(240,180,41,0.2)' }}>
          <FaExclamationTriangle className="text-qfl-gold flex-shrink-0 mt-0.5" size={11} />
          <p className="text-white/70 text-xs leading-relaxed">{q.review_note || 'Aucun motif enregistré'}</p>
        </div>
      </button>

      {/* Formulaire de correction */}
      {ouvert && (
        <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-3">
          <div>
            <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1 block">Énoncé</label>
            <textarea
              value={form.intitule}
              onChange={(e) => setForm({ ...form, intitule: e.target.value })}
              rows={2}
              className="form-input w-full text-sm"
            />
          </div>

          <div>
            <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1 block">
              Choix — coche la bonne réponse
            </label>
            <div className="space-y-2">
              {form.choix.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <button
                    onClick={() => setForm({ ...form, bonne_reponse: i })}
                    title="Désigner comme bonne réponse"
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                    style={{
                      background: form.bonne_reponse === i ? '#22c55e' : 'rgba(255,255,255,0.06)',
                      color:      form.bonne_reponse === i ? '#06240f' : 'rgba(255,255,255,0.3)',
                    }}
                  >
                    {form.bonne_reponse === i ? <FaCheck size={11} /> : String.fromCharCode(65 + i)}
                  </button>
                  <input
                    value={c}
                    onChange={(e) => {
                      const n = [...form.choix];
                      n[i] = e.target.value;
                      setForm({ ...form, choix: n });
                    }}
                    className="form-input flex-1 text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1 block">Catégorie</label>
              <select value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value })}
                className="form-input w-full text-sm">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1 block">Difficulté</label>
              <select value={form.difficulte} onChange={(e) => setForm({ ...form, difficulte: e.target.value })}
                className="form-input w-full text-sm">
                {DIFFICULTES.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              onClick={() => enregistrer(true)}
              disabled={saving}
              className="flex-1 min-w-[160px] py-2.5 rounded-xl font-black text-xs uppercase tracking-widest disabled:opacity-50"
              style={{ fontFamily: "'Rajdhani', sans-serif", background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: '#06240f' }}
            >
              ✅ Corriger et remettre en jeu
            </button>
            {modifie && (
              <button
                onClick={() => enregistrer(false)}
                disabled={saving}
                className="py-2.5 px-4 rounded-xl font-bold text-xs uppercase tracking-widest text-white/70 bg-white/5 border border-white/10 disabled:opacity-50"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                Enregistrer sans réactiver
              </button>
            )}
            <button
              onClick={supprimer}
              disabled={saving}
              title="Supprimer définitivement"
              className="py-2.5 px-3 rounded-xl text-red-400/70 hover:text-red-400 bg-red-500/5 border border-red-500/20 disabled:opacity-50"
            >
              <FaTrash size={12} />
            </button>
          </div>
          <p className="text-white/25 text-[10px] font-mono">{q.id}</p>
        </div>
      )}
    </div>
  );
}

function AdminQuestionsReview() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [recherche, setRecherche] = useState('');
  const [categorie, setCategorie] = useState('toutes');
  const [difficulte, setDifficulte] = useState('toutes');

  const charger = () => {
    api.get('/questions/review')
      .then(({ data }) => setQuestions(data.questions || []))
      .catch(() => toast.error('Impossible de charger les questions'))
      .finally(() => setLoading(false));
  };

  useEffect(charger, []);

  const retirerDeLaListe = (id) => setQuestions((prev) => prev.filter((q) => q.id !== id));

  const filtrees = useMemo(() => {
    const r = recherche.trim().toLowerCase();
    return questions.filter((q) => {
      if (categorie !== 'toutes' && q.categorie !== categorie) return false;
      if (difficulte !== 'toutes' && q.difficulte !== difficulte) return false;
      if (!r) return true;
      const choix = Array.isArray(q.choix) ? q.choix.join(' ') : String(q.choix || '');
      return `${q.intitule} ${choix} ${q.review_note || ''}`.toLowerCase().includes(r);
    });
  }, [questions, recherche, categorie, difficulte]);

  const parCategorie = useMemo(() => {
    const m = {};
    questions.forEach((q) => { m[q.categorie] = (m[q.categorie] || 0) + 1; });
    return m;
  }, [questions]);

  return (
    <div className="app-container min-h-screen px-4 pb-10">
      <div className="flex items-center gap-3 pt-5 pb-4">
        <button onClick={() => navigate('/admin')} className="text-white/50 hover:text-white">
          <FaArrowLeft size={16} />
        </button>
        <div>
          <h1 className="text-qfl-gold font-black text-lg uppercase tracking-wider"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            Questions à revoir
          </h1>
          <p className="text-white/40 text-xs">
            {loading ? 'Chargement…' : `${questions.length} question${questions.length > 1 ? 's' : ''} retirée${questions.length > 1 ? 's' : ''} de la rotation`}
          </p>
        </div>
      </div>

      {!loading && questions.length > 0 && (
        <>
          <div className="flex gap-2 flex-wrap mb-3 text-[11px]">
            {Object.entries(parCategorie).map(([c, n]) => (
              <span key={c} className="px-2.5 py-1 rounded-full bg-white/5 text-white/50 uppercase tracking-wider">
                {c} : <strong className="text-white/80">{n}</strong>
              </span>
            ))}
          </div>

          <div className="relative mb-3">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25" size={12} />
            <input
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="Rechercher un énoncé, un choix, un motif…"
              className="form-input w-full text-sm pl-9"
            />
          </div>

          <div className="flex gap-2 mb-4 flex-wrap">
            <select value={categorie} onChange={(e) => setCategorie(e.target.value)}
              className="form-input text-xs flex-1 min-w-[130px]">
              <option value="toutes">Toutes catégories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={difficulte} onChange={(e) => setDifficulte(e.target.value)}
              className="form-input text-xs flex-1 min-w-[130px]">
              <option value="toutes">Toutes difficultés</option>
              {DIFFICULTES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          <p className="text-white/35 text-xs mb-3">
            {filtrees.length} affichée{filtrees.length > 1 ? 's' : ''}
          </p>
        </>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-qfl-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : questions.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
          <div className="text-5xl mb-3">🎉</div>
          <p className="text-white font-bold">Plus aucune question à revoir !</p>
          <p className="text-white/40 text-sm mt-1">Toutes les questions sont en jeu.</p>
        </motion.div>
      ) : (
        <div className="space-y-2.5">
          {filtrees.map((q) => (
            <QuestionCard key={q.id} q={q} onSaved={retirerDeLaListe} onDeleted={retirerDeLaListe} />
          ))}
          {filtrees.length === 0 && (
            <p className="text-white/35 text-sm text-center py-10">Aucune question ne correspond à ce filtre.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminQuestionsReview;
